import { useEffect } from 'react';
import {
  generateOrganizationSchema,
  generateWebSiteSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateServiceSchema,
  generateArticleSchema
} from '../utils/seo';

/**
 * StructuredData Component
 * Injects JSON-LD structured data into the page head for SEO
 */
const StructuredData = ({
  type = 'WebPage',
  data = {},
  breadcrumbs = null,
  includeOrganization = true,
  includeWebSite = false
}) => {
  useEffect(() => {
    const scripts = [];

    // Generate Organization schema if needed
    if (includeOrganization && data.organization) {
      const orgSchema = generateOrganizationSchema(data.organization);
      const orgScript = createStructuredDataScript(orgSchema, 'organization');
      scripts.push(orgScript);
    }

    // Generate WebSite schema if needed
    if (includeWebSite && data.site) {
      const siteSchema = generateWebSiteSchema(data.site);
      const siteScript = createStructuredDataScript(siteSchema, 'website');
      scripts.push(siteScript);
    }

    // Generate BreadcrumbList schema if provided (only if breadcrumbs are explicitly passed)
    if (breadcrumbs !== null && breadcrumbs !== undefined && breadcrumbs.length > 0) {
      const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
      if (breadcrumbSchema) {
        const breadcrumbScript = createStructuredDataScript(breadcrumbSchema, 'breadcrumbs');
        scripts.push(breadcrumbScript);
      }
    }

    // Generate page-specific schema based on type
    let pageSchema = null;
    switch (type) {
      case 'WebPage':
        pageSchema = generateWebPageSchema(data.page || {});
        break;
      case 'Service':
        pageSchema = generateServiceSchema(data.service || {});
        break;
      case 'Article':
        pageSchema = generateArticleSchema(data.article || {});
        break;
      default:
        pageSchema = generateWebPageSchema(data.page || {});
    }

    if (pageSchema) {
      const pageScript = createStructuredDataScript(pageSchema, 'page');
      scripts.push(pageScript);
    }

    // Append all scripts to head
    scripts.forEach(script => {
      document.head.appendChild(script);
    });

    // Cleanup: remove scripts when component unmounts or data changes
    return () => {
      scripts.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [type, data, breadcrumbs, includeOrganization, includeWebSite]);

  return null; // This component doesn't render anything
};

/**
 * Helper function to create a script tag with JSON-LD structured data
 */
const createStructuredDataScript = (schema, id) => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = `structured-data-${id}`;
  script.textContent = JSON.stringify(schema, null, 0);
  return script;
};

export default StructuredData;
